import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Settings,
  ChevronDown,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function OPDProfileForm() {
  const initialState = {
    name: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    address: "Addis Ababa",
    sub_city: "",
    established_date: "",
  };

  const subcities = [
    "አዲስ ከተማ", "አቃቂ ቃሊቲ", "አራዳ", "ቦሌ", "ጉለሌ", 
    "ቂርቆስ", "ኮልፌ ቀራኒዮ", "ለሚ ኩራ", "ልደታ", "ንፋስ ስልክ ላፍቶ", "የካ"
  ];

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [lockSubCity, setLockSubCity] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [loading, setLoading] = useState(true);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    async function loadProfile() {
      setLoading(true);
      try {
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        const currentUserEmail = currentUser?.email;

        if (!currentUserEmail) {
          setSubmitStatus('error');
          setErrors({ submit: 'No logged-in user found.' });
          return;
        }

        // Fetch OPD by email
        const response = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
        const data = await response.json();

        if (data.status === 'success' && data.data) {
          const opdData = data.data;
          setFormData({
            ...initialState,
            ...opdData,
            // Always force address to Addis Ababa
            address: "Addis Ababa",
            established_date: opdData.established_date
              ? opdData.established_date.slice(0, 10)
              : "",
          });
          setProfileId(opdData.id);
          if (opdData.sub_city) {
            setLockSubCity(true);
          }
        } else {
          setFormData({
            ...initialState,
            contact_email: currentUserEmail,
          });
          setProfileId(null);
        }
      } catch (error) {
        console.error("Failed to load OPD profile:", error);
        setSubmitStatus('error');
        setErrors({ submit: 'Failed to load profile' });
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    setSubmitStatus(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "OPD Name is required";
    if (!formData.contact_person.trim())
      newErrors.contact_person = "Contact Person is required";
    if (!formData.contact_phone.trim())
      newErrors.contact_phone = "Contact Phone is required";
    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email))
      newErrors.contact_email = "Invalid email address";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSubmitStatus('error');
      setErrors({ ...errors, submit: 'Please fill all required fields correctly' });
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      let response;

      if (profileId) {
        // Update existing OPD
        response = await fetch(`${API_BASE_URL}/update_opd.php?id=${profileId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            contact_email_original: formData.contact_email,
          }),
        });
      } else {
        // Create new OPD - this shouldn't happen as OPDs should be created through admin
        response = await fetch(`${API_BASE_URL}/add_opd.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contactEmail: formData.contact_email,
            password: 'defaultPassword123', // This should be handled differently
          }),
        });
      }

      const data = await response.json();

      if (data.status === 'success') {
        setSubmitStatus('success');
        setErrors({ submit: profileId ? 'Profile updated successfully!' : 'Profile saved successfully!' });

        // If it was a new profile, set the profileId
        if (!profileId && data.opd_id) {
          setProfileId(data.opd_id);
        }

        // Once a Sub City is saved for a profile, lock it from future edits
        if (formData.sub_city) {
          setLockSubCity(true);
        }
      } else {
        setSubmitStatus('error');
        setErrors({ submit: data.message || 'Failed to save profile. Please try again.' });
      }
    } catch (error) {
      console.error("Error saving OPD profile:", error);
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to save profile. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const formFields = [
    { id: "name", label: "OPD Name", required: true, type: "text", icon: Building2 },
    { id: "contact_person", label: "Contact Person", required: true, type: "text", icon: User },
    { id: "contact_phone", label: "Contact Phone", required: true, type: "tel", icon: Phone },
    { id: "contact_email", label: "Contact Email", required: false, type: "email", readOnly: true, icon: Mail },
    { id: "address", label: "Address", type: "text", icon: MapPin },
    { id: "sub_city", label: "Sub City", required: true, type: "select", options: subcities, icon: MapPin },
    { id: "established_date", label: "Established Date", type: "date", icon: Calendar },
  ];

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen p-4 md:p-8">
        <div className="text-center py-16">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500 text-lg">Loading OPD profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-4xl font-bold text-gray-800">OPD Profile</h1>
              <p className="text-gray-500">Manage your organization's profile information</p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Organization Information</h2>
                <p className="text-teal-100 mt-1">Update your OPD details and contact information</p>
              </div>
            </div>
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors shadow-sm"
              >
                <Settings className="w-4 h-4" />
                Settings
                <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-12 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-48 overflow-hidden">
                  <button
                    onClick={() => {
                      navigate("/opdemailupdate");
                      setDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600 hover:text-red-700"
                  >
                    <Mail className="w-4 h-4 text-red-500" />
                    Update Email
                  </button>
                  <button
                    onClick={() => {
                      navigate("/opdpasswordupdate");
                      setDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-2 border-t border-gray-100 text-red-600 hover:text-red-700"
                  >
                    <Settings className="w-4 h-4 text-red-500" />
                    Update Password
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-green-800 font-semibold">Success!</p>
                <p className="text-green-700 text-sm">{errors.submit}</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-red-800 font-semibold">Error</p>
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-200">
                <Building2 className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {formFields.slice(0, 4).map((field) => {
                  const Icon = field.icon;
                  return (
                    <div key={field.id} className="">
                      <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center gap-1">
                          <Icon className="w-4 h-4" />
                          {field.label} {field.required ? "*" : ""}
                        </div>
                      </label>
                      
                      <input
                        id={field.id}
                        name={field.id}
                        type={field.type}
                        value={formData[field.id]}
                        onChange={handleChange}
                        required={field.required}
                        readOnly={field.readOnly}
                        className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                          errors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        } ${field.readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                      
                      {errors[field.id] && (
                        <p className="text-red-600 text-sm mt-1">{errors[field.id]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Address Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-200">
                <MapPin className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-800">Address Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {formFields.slice(4, 9).map((field) => {
                  const Icon = field.icon;
                  return (
                    <div key={field.id} className="">
                      <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center gap-1">
                          <Icon className="w-4 h-4" />
                          {field.label} {field.required ? "*" : ""}
                        </div>
                      </label>
                      
                      {field.type === 'select' ? (
                        <select
                          id={field.id}
                          name={field.id}
                          value={formData[field.id]}
                          onChange={handleChange}
                          required={field.required}
                          disabled={field.readOnly || (field.id === 'sub_city' && lockSubCity)}
                          className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white ${
                            errors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          } ${(field.readOnly || (field.id === 'sub_city' && lockSubCity)) ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        >
                          <option value="">Select {field.label}</option>
                          {field.options.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={field.id}
                          name={field.id}
                          type={field.type}
                          value={formData[field.id]}
                          onChange={handleChange}
                          required={field.required}
                          readOnly={field.readOnly || (field.id === 'address' && profileId && !!formData.address)}
                          className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                            errors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          } ${(field.readOnly || (field.id === 'address' && profileId && !!formData.address)) ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                      )}
                      
                      {errors[field.id] && (
                        <p className="text-red-600 text-sm mt-1">{errors[field.id]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-semibold py-3 px-8 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {profileId ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {profileId ? "Update Profile" : "Save Profile"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
